import React, { useEffect, useState } from "react";
import { X, Trophy, Gamepad2, Box } from "lucide-react";
import { api } from "@/lib/api";
import { sfx } from "../citySfx";
import QuackManGame from "@/components/app/QuackManGame";
import GameHost from "./GameHost";
import { ARCADE_GAMES, ARCADE_CATS } from "./arcadeRegistry";

export default function ArcadeLobby({ open, onClose, onCoin, playerName, initialGame }) {
  const [active, setActive] = useState(null); // null | "quackman" | game entry
  const [records, setRecords] = useState({});

  useEffect(() => {
    if (!open) return;
    if (initialGame) { const g = ARCADE_GAMES.find((x) => x.id === initialGame); setActive(g ? (g.special === "quackman" ? "quackman" : g) : null); } else setActive(null);
    api.get("/arcade/scores/all").then(({ data }) => setRecords(data.records || {})).catch(() => {});
    const onKey = (e) => { if (e.key === "Escape") { if (active) { setActive(null); } else onClose?.(); } };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    // eslint-disable-line
  }, [open, active]); // eslint-disable-line

  if (!open) return null;

  const play = (g) => { sfx.door?.(); setActive(g.special === "quackman" ? "quackman" : g); };

  return (
    <div className="fixed inset-0 z-[110] bg-[#05060f]" data-testid="arcade-lobby" style={{ fontFamily: "Manrope, system-ui" }}>
      <style>{`.arc-cab{transition:transform .2s cubic-bezier(.2,.8,.2,1),box-shadow .2s}.arc-cab:hover{transform:translateY(-4px)}.arc-scan{background:repeating-linear-gradient(0deg,rgba(255,255,255,.06),rgba(255,255,255,.06) 1px,transparent 1px,transparent 4px)}`}</style>

      {active ? (
        <div className="h-full w-full flex flex-col">
          {active === "quackman" ? (
            <div className="h-full w-full flex flex-col">
              <div className="flex items-center gap-2 px-2 py-2 shrink-0">
                <button onClick={() => setActive(null)} className="cq-px text-[9px] px-3 py-2 border border-white/20 text-slate-200 hover:bg-white/10" data-testid="arcade-back-lobby">◄ LOBBY</button>
                <p className="flex-1 text-center cq-px text-amber-300 text-[10px]">QUACKMAN</p>
                <div className="w-16" />
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto"><QuackManGame /></div>
            </div>
          ) : (
            <GameHost game={active} playerName={playerName} onExit={() => setActive(null)} onCoin={onCoin} />
          )}
        </div>
      ) : (
        <div className="h-full w-full overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#05060f]/90 backdrop-blur border-b border-cyan-400/20 px-4 py-3 flex items-center gap-3">
            <Gamepad2 className="text-cyan-300" size={20} />
            <div className="flex-1">
              <p className="cq-px text-cyan-300 text-[12px] sm:text-sm">QUACK ARCADE</p>
              <p className="text-slate-400 text-[11px] mt-0.5">{ARCADE_GAMES.length} games · earn coins · top the leaderboards</p>
            </div>
            <button onClick={() => { sfx.close?.(); onClose(); }} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" aria-label="Exit arcade" data-testid="arcade-exit"><X size={18} /></button>
          </div>

          <div className="max-w-6xl mx-auto px-4 py-6 space-y-9">
            {ARCADE_CATS.map((cat) => {
              const list = ARCADE_GAMES.filter((g) => g.cat === cat);
              if (!list.length) return null;
              return (
                <div key={cat}>
                  <div className="flex items-center gap-2 mb-4">
                    {cat === "3D Zone" ? <Box size={14} className="text-cyan-300" /> : <span className="w-2 h-2 rounded-full bg-cyan-300" />}
                    <p className="cq-px text-[10px] text-slate-200">{cat}</p>
                    <span className="flex-1 h-px bg-white/10" />
                    <span className="cq-px text-[8px] text-slate-500">{list.length}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {list.map((g) => {
                      const rec = records[g.id];
                      return (
                        <button key={g.id} onClick={() => play(g)} className="arc-cab group text-left rounded-xl overflow-hidden border-2 bg-[#0a0f1e]" style={{ borderColor: g.accent + "66" }} data-testid={`arcade-cabinet-${g.id}`}>
                          {/* Marquee */}
                          <div className="px-3 py-2 text-center" style={{ background: `linear-gradient(180deg, ${g.accent}, ${g.accent}bb)` }}>
                            <p className="cq-px text-[8px] text-slate-950 leading-tight">{g.name}</p>
                          </div>
                          {/* Screen */}
                          <div className="relative aspect-[4/3] arc-scan grid place-items-center" style={{ background: `radial-gradient(circle at 50% 40%, ${g.accent}33, #05060f 75%)` }}>
                            <div className="w-12 h-12 rounded-lg grid place-items-center" style={{ background: g.accent + "22", border: `1px solid ${g.accent}66` }}>
                              {g.dim === "3d" ? <Box size={22} style={{ color: g.accent }} /> : <Gamepad2 size={22} style={{ color: g.accent }} />}
                            </div>
                            <span className="absolute bottom-1 right-2 cq-px text-[7px]" style={{ color: g.accent }}>▶ PLAY</span>
                          </div>
                          {/* Base */}
                          <div className="px-3 py-2 bg-black/40">
                            <p className="text-[11px] text-slate-300 leading-snug line-clamp-2 min-h-[30px]">{g.tag}</p>
                            <p className="mt-1 flex items-center gap-1 cq-px text-[7px] text-amber-300"><Trophy size={9} />{rec ? `${rec.name} · ${rec.score}` : "BE THE FIRST"}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <p className="text-center text-slate-500 text-[11px] pb-4">Tip: on a phone, use the on-screen buttons. On a computer, use the arrow keys and Space.</p>
          </div>
        </div>
      )}
    </div>
  );
}
